import { useCallback, useEffect, useRef, useState } from 'react'

import {
  NEW_STORES_BLANKSTREET,
  NEW_STORES_DEV,
  SAME_STORES_BLANKSTREET,
  SAME_STORES_DEV,
} from '@/constants'
import { HierarchyNode, Region } from '@/types'

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

export type BranchType = 'all' | 'same' | 'new' | 'regular'

export type Selection = {
  selectedRegions: number[]
  selectedBranches: string[]
  selectedBranchNames: string[]
  branchType: BranchType
  predefinedSelection?: 'all' | 'same' | 'new' | null
}

export interface BasicBranchSelectProps {
  onChange?: (selection: Selection) => void
  value?: string
  branchType?: BranchType
  resetBranchSelect?: React.MutableRefObject<boolean>
  branchOnly?: boolean
}

// ----------------------------------------------------------------------
// HELPER FUNCTIONS
// ----------------------------------------------------------------------

export const calculateFullySelectedRegions = (
  branchIds: string[],
  regions: Region[]
) => {
  return regions
    .filter((region) => {
      if (region.branches.length === 0) return false
      return region.branches.every((branch) => branchIds.includes(branch.id))
    })
    .map((region) => region.id)
}

export const findBranch = (branchId: string, regions: Region[]) =>
  regions.flatMap((r) => r.branches).find((b) => b.id === branchId)

export const findRegion = (regionId: number, regions: Region[]) =>
  regions.find((r) => r.id === regionId)

export const findBranchRegion = (branchId: string, regions: Region[]) =>
  regions.find((r) => r.branches.some((b) => b.id === branchId))

// Detect if the current selection matches a predefined set (All/Same/New)
export const detectPredefinedSelection = (
  branchNames: string[],
  selectableRegions: Region[]
): 'all' | 'same' | 'new' | null => {
  if (branchNames.length === 0) return null

  const allBranches = selectableRegions.flatMap((r) =>
    r.branches.map((b) => b.name)
  )

  const sameBranches = SAME_STORES_DEV
  const newBranches = NEW_STORES_DEV

  const sortedSelection = [...branchNames].sort()
  const sortedAll = [...allBranches].sort()
  const sortedSame = [...sameBranches].sort()
  const sortedNew = [...newBranches].sort()

  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedAll))
    return 'all'
  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedSame))
    return 'same'
  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedNew))
    return 'new'

  return null
}

// Create a selection object for a predefined type
export const createPredefinedSelection = (
  type: 'all' | 'same' | 'new',
  regions: Region[]
): Selection => {
  let branches: { id: string; name: string }[] = []

  if (type === 'all') {
    branches = regions.flatMap((r) => r.branches)
  } else if (type === 'same') {
    branches = regions.flatMap((r) =>
      r.branches.filter((b) => SAME_STORES_DEV.includes(b.name))
    )
  } else if (type === 'new') {
    branches = regions.flatMap((r) =>
      r.branches.filter((b) => NEW_STORES_DEV.includes(b.name))
    )
  }

  const selectedBranchIds = branches.map((b) => b.id)

  return {
    selectedRegions: calculateFullySelectedRegions(selectedBranchIds, regions),
    selectedBranches: selectedBranchIds,
    selectedBranchNames: branches.map((b) => b.name),
    branchType: type,
    predefinedSelection: type,
  }
}

export const emptySelection: Selection = {
  selectedRegions: [],
  selectedBranches: [],
  selectedBranchNames: [],
  branchType: 'regular',
  predefinedSelection: null,
}

// ----------------------------------------------------------------------
// CUSTOM HOOK
// ----------------------------------------------------------------------

export const useBranchSelection = ({
  regions,
  selectableRegions,
  value,
  branchType,
  tenant,
  onChange,
  resetBranchSelect,
}: {
  regions: Region[]
  selectableRegions: Region[]
  value?: string
  branchType?: BranchType
  tenant: string
  onChange?: (selection: Selection) => void
  resetBranchSelect?: React.MutableRefObject<boolean>
}) => {
  const [selection, setSelection] = useState<Selection>(emptySelection)
  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(new Set())
  const hasInitialized = useRef(false)

  const getDerivedSelection = useCallback((): Selection => {
    if (regions.length === 0) return emptySelection

    if (branchType === 'same')
      return createPredefinedSelection('same', selectableRegions)
    if (branchType === 'all') {
      return createPredefinedSelection('all', selectableRegions)
    }
    if (branchType === 'new')
      return createPredefinedSelection('new', selectableRegions)

    if (value) {
      const branchNames = value.split(',').map((name) => name.trim())
      const matchedBranches = regions
        .flatMap((r) => r.branches)
        .filter((b) => branchNames.includes(b.name))

      if (matchedBranches.length > 0) {
        const selectedBranchIds = matchedBranches.map((b) => b.id)
        return {
          selectedRegions: calculateFullySelectedRegions(
            selectedBranchIds,
            regions
          ),
          selectedBranches: selectedBranchIds,
          selectedBranchNames: matchedBranches.map((b) => b.name),
          branchType: 'regular',
          predefinedSelection: null,
        }
      }
    }

    if (!value && !branchType) {
      const defaultType = tenant === 'blankstreet' ? 'all' : 'same'
      if (defaultType === 'same') {
        return createPredefinedSelection('same', selectableRegions)
      }
      if (defaultType === 'all') {
        return createPredefinedSelection('all', selectableRegions)
      }
    }

    return emptySelection
  }, [regions, selectableRegions, value, branchType, tenant])

  useEffect(() => {
    if (regions.length === 0) return

    const newSelection = getDerivedSelection()

    const isDifferent =
      newSelection.branchType !== selection.branchType ||
      JSON.stringify(newSelection.selectedBranches.sort()) !==
        JSON.stringify(selection.selectedBranches.sort())

    if (isDifferent) {
      setSelection(newSelection)
      if (!hasInitialized.current) {
        hasInitialized.current = true
        if (newSelection.selectedBranches.length > 0) {
          onChange?.(newSelection)
        }
      }
    }
  }, [getDerivedSelection, regions.length])

  // Reset
  useEffect(() => {
    if (resetBranchSelect?.current && regions.length > 0) {
      const finalResetSelection = getDerivedSelection()

      setSelection(finalResetSelection)
      setExpandedRegions(new Set())
      resetBranchSelect.current = false
      onChange?.(finalResetSelection)
    }
  }, [resetBranchSelect, regions, tenant, getDerivedSelection, onChange])

  const updateSelection = useCallback(
    (newSelection: Selection) => {
      const detectedPredefined = detectPredefinedSelection(
        newSelection.selectedBranchNames,
        selectableRegions
      )

      let determinedBranchType: BranchType = 'regular'
      if (detectedPredefined) determinedBranchType = detectedPredefined

      const finalSelection = {
        ...newSelection,
        branchType: determinedBranchType,
        predefinedSelection: detectedPredefined,
      }

      setSelection(finalSelection)
      onChange?.(finalSelection)
    },
    [selectableRegions, onChange]
  )

  const handlePredefinedSelect = useCallback(
    (type: 'all' | 'same' | 'new', checked: boolean) => {
      if (checked) {
        updateSelection(createPredefinedSelection(type, selectableRegions))
      } else {
        updateSelection(emptySelection)
      }
    },
    [selectableRegions, updateSelection]
  )

  const handleRegionSelect = useCallback(
    (regionId: number, checked: boolean) => {
      const region = findRegion(regionId, regions)
      if (!region) return

      const branchIds = region.branches.map((b) => b.id)
      const branchNames = region.branches.map((b) => b.name)

      let newBranchIds: string[]
      let newBranchNames: string[]
      let newRegionIds: number[]

      if (checked) {
        // Add
        newBranchIds = [
          ...new Set([...selection.selectedBranches, ...branchIds]),
        ]
        newBranchNames = [
          ...new Set([...selection.selectedBranchNames, ...branchNames]),
        ]
        newRegionIds = [...new Set([...selection.selectedRegions, regionId])]
      } else {
        // Remove
        newBranchIds = selection.selectedBranches.filter(
          (id) => !branchIds.includes(id)
        )
        newBranchNames = selection.selectedBranchNames.filter(
          (name) => !branchNames.includes(name)
        )
        newRegionIds = selection.selectedRegions.filter((id) => id !== regionId)
      }

      updateSelection({
        selectedRegions: newRegionIds,
        selectedBranches: newBranchIds,
        selectedBranchNames: newBranchNames,
        branchType: 'regular',
        predefinedSelection: null,
      })
    },
    [selection, regions, updateSelection]
  )

  const handleBranchSelect = useCallback(
    (regionId: number, branchId: string, checked: boolean) => {
      const branch = findBranch(branchId, regions)
      if (!branch) return

      let newBranchIds: string[]
      let newBranchNames: string[]

      if (checked) {
        newBranchIds = [...selection.selectedBranches, branchId]
        newBranchNames = [...selection.selectedBranchNames, branch.name]
      } else {
        newBranchIds = selection.selectedBranches.filter(
          (id) => id !== branchId
        )
        newBranchNames = selection.selectedBranchNames.filter(
          (name) => name !== branch.name
        )
      }

      // Check if region is now fully selected
      const region = findRegion(regionId, regions)
      const isRegionFull =
        region?.branches.every((b) => newBranchIds.includes(b.id)) ?? false

      let newRegionIds = selection.selectedRegions.filter(
        (id) => id !== regionId
      )
      if (isRegionFull) {
        newRegionIds.push(regionId)
      }

      updateSelection({
        selectedRegions: newRegionIds,
        selectedBranches: newBranchIds,
        selectedBranchNames: newBranchNames,
        branchType: 'regular',
        predefinedSelection: null,
      })
    },
    [selection, regions, updateSelection]
  )

  useEffect(() => {
    if (
      selectableRegions.length === 0 ||
      selection.selectedBranches.length === 0
    )
      return

    const firstBranchId = selection.selectedBranches[0]
    const region = findBranchRegion(firstBranchId, selectableRegions)

    if (region && !expandedRegions.has(region.id)) {
      setExpandedRegions((prev) => new Set([...prev, region.id]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableRegions])

  const toggleRegionExpansion = useCallback((regionId: number) => {
    setExpandedRegions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(regionId)) newSet.delete(regionId)
      else newSet.add(regionId)
      return newSet
    })
  }, [])

  return {
    selection,
    expandedRegions,
    handlePredefinedSelect,
    handleRegionSelect,
    handleBranchSelect,
    toggleRegionExpansion,
  }
}

// ----------------------------------------------------------------------
// HIERARCHY NODE UTILITIES (for multi-level branch selection)
// ----------------------------------------------------------------------

/**
 * Convert Region[] to HierarchyNode[] for backward compatibility
 */
export const regionsToHierarchy = (regions: Region[]): HierarchyNode[] => {
  return regions.map((region) => ({
    id: `region-${region.id}`,
    name: region.name,
    level: 'region',
    children: region.branches.map((branch) => ({
      id: String(branch.id),
      name: branch.name,
      level: 'store',
    })),
  }))
}

/**
 * Check if a node is a leaf node (has no children)
 */
export const isLeafNode = (node: HierarchyNode): boolean => {
  return !node.children || node.children.length === 0
}

/**
 * Get all leaf nodes from a hierarchy tree
 */
export const getAllLeafNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
  const leaves: HierarchyNode[] = []

  const traverse = (node: HierarchyNode) => {
    if (isLeafNode(node)) {
      leaves.push(node)
    } else {
      node.children?.forEach(traverse)
    }
  }

  nodes.forEach(traverse)
  return leaves
}

/**
 * Get all descendant IDs of a node (including the node itself)
 */
export const getAllDescendantIds = (node: HierarchyNode): string[] => {
  const ids: string[] = [node.id]

  const traverse = (n: HierarchyNode) => {
    if (n.children) {
      n.children.forEach((child) => {
        ids.push(child.id)
        traverse(child)
      })
    }
  }

  traverse(node)
  return ids
}

/**
 * Get all leaf node IDs from a node
 */
export const getLeafNodeIds = (node: HierarchyNode): string[] => {
  if (isLeafNode(node)) {
    return [node.id]
  }

  const ids: string[] = []
  const traverse = (n: HierarchyNode) => {
    if (isLeafNode(n)) {
      ids.push(n.id)
    } else {
      n.children?.forEach(traverse)
    }
  }

  traverse(node)
  return ids
}

/**
 * Find a node by ID in the hierarchy tree
 */
export const findNodeById = (
  nodes: HierarchyNode[],
  id: string
): HierarchyNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

/**
 * Find the parent node of a given node ID
 */
export const findParentNode = (
  nodes: HierarchyNode[],
  childId: string,
  parent: HierarchyNode | null = null
): HierarchyNode | null => {
  for (const node of nodes) {
    if (node.id === childId) return parent
    if (node.children) {
      const found = findParentNode(node.children, childId, node)
      if (found) return found
    }
  }
  return null
}

/**
 * Get all unique level names from the hierarchy
 */
export const getAllLevels = (nodes: HierarchyNode[]): string[] => {
  const levels = new Set<string>()

  const traverse = (node: HierarchyNode) => {
    levels.add(node.level)
    node.children?.forEach(traverse)
  }

  nodes.forEach(traverse)
  return Array.from(levels)
}

/**
 * Search leaf nodes by name (case-insensitive)
 */
export const searchLeafNodes = (
  nodes: HierarchyNode[],
  query: string
): HierarchyNode[] => {
  if (!query.trim()) return []

  const lowerQuery = query.toLowerCase()
  const leaves = getAllLeafNodes(nodes)

  return leaves.filter((leaf) => leaf.name.toLowerCase().includes(lowerQuery))
}

/**
 * Check if all children of a node are selected
 */
export const areAllChildrenSelected = (
  node: HierarchyNode,
  selectedIds: Set<string>
): boolean => {
  if (isLeafNode(node)) {
    return selectedIds.has(node.id)
  }

  return (
    node.children?.every((child) =>
      areAllChildrenSelected(child, selectedIds)
    ) ?? false
  )
}

/**
 * Get path from root to a node
 */
export const getPathToNode = (
  nodes: HierarchyNode[],
  targetId: string,
  currentPath: HierarchyNode[] = []
): HierarchyNode[] | null => {
  for (const node of nodes) {
    const newPath = [...currentPath, node]
    if (node.id === targetId) return newPath
    if (node.children) {
      const found = getPathToNode(node.children, targetId, newPath)
      if (found) return found
    }
  }
  return null
}

// ----------------------------------------------------------------------
// HIERARCHY SELECTION TYPES
// ----------------------------------------------------------------------

export type HierarchySelection = {
  selectedIds: Set<string>
  selectedLeafIds: Set<string>
  selectedLeafNames: string[]
  branchType: BranchType
  predefinedSelection: 'all' | 'same' | 'new' | null
}

export const emptyHierarchySelection: HierarchySelection = {
  selectedIds: new Set(),
  selectedLeafIds: new Set(),
  selectedLeafNames: [],
  branchType: 'regular',
  predefinedSelection: null,
}

/**
 * Create a predefined selection for HierarchyNode[] (for multi-level branch selection)
 */
export const createPredefinedHierarchySelection = (
  type: 'all' | 'same' | 'new',
  nodes: HierarchyNode[],
  tenant: string
): HierarchySelection => {
  const allIds = new Set<string>()
  const allLeafIds = new Set<string>()
  const allLeafNames: string[] = []

  // Get all leaf nodes
  const allLeaves = getAllLeafNodes(nodes)

  // Filter based on type
  let filteredLeaves: HierarchyNode[] = allLeaves

  if (type === 'same') {
    const sameStores =
      tenant === 'blankstreet' ? SAME_STORES_BLANKSTREET : SAME_STORES_DEV
    filteredLeaves = allLeaves.filter((leaf) => sameStores.includes(leaf.name))
  } else if (type === 'new') {
    const newStores =
      tenant === 'blankstreet' ? NEW_STORES_BLANKSTREET : NEW_STORES_DEV
    filteredLeaves = allLeaves.filter((leaf) => newStores.includes(leaf.name))
  }

  // Add all ancestors of filtered leaves
  const addAncestors = (leaf: HierarchyNode) => {
    const path = getPathToNode(nodes, leaf.id)
    if (path) {
      path.forEach((node) => {
        allIds.add(String(node.id))
      })
    }
    allLeafIds.add(String(leaf.id))
    allLeafNames.push(leaf.name)
  }

  filteredLeaves.forEach(addAncestors)

  return {
    selectedIds: allIds,
    selectedLeafIds: allLeafIds,
    selectedLeafNames: allLeafNames,
    branchType: type,
    predefinedSelection: type,
  }
}
